
import { getUberShader } from "./UberShader";
import { getBasicShader } from "./BasicShader";
import { DepthFormat, invertDepthCompare } from "../CommonRenderTargets";
import { getBufferLayout, getPipelineHash, isValidWideLineGeometry } from "../Pipelines";
import { sideToCullMode } from "../compat";
import { initMaterialBindings, MaterialUniformFlags, syncMaterialTextureBindings } from "./MaterialUniforms";
import { ToneMapMethod } from "../Renderer";
import { getDepthBiasValues } from "./DepthBiasMap";

/** @import { VertexBuffer } from "../VertexBuffer" */
/** @import { PointDataBuffer } from "../PointDataBuffer" */
/** @import { MultiDrawBatch } from "../gpudrivenrender/MultiDrawBatch" */

const CustomTextureBindGroup = 3;
// GPU skinning uses the same slot as custom textures; the two never apply to the same material.
const SkinningBindGroup = 3;

export class UberPipeline {

  #renderer;
  /** @type {GPUDevice} */
  #device;

  #pipelines = new Map();
  /** Ghosted (dimmed) pass pipelines; */
  #ghostedPipelines = new Map();
  /** @type {GPUBindGroupLayout[]} */
  #activeBindGroupLayout = [];
  /** @type {GPUColorTargetState[]} */
  #activeTargetsList = [];
  /** @type {VertexBuffer} */
  #vb;
  /** @type {PointDataBuffer} */
  #pointDataBuffer;
  /** @type {GPUBindGroup} */
  #defaultMaterialBindGroup;
  /** @type {GPUBindGroupLayout|undefined} */
  #pointDataBindGroupLayout;
  /** @type {GPUBindGroupLayout|null} */
  #skinningBindGroupLayout = null;

  /**
   * @param {Renderer} renderer
   * @param {GPUBindGroup} defaultMaterialBindGroup - Default bind group for materials without textures
   */
  constructor(renderer, defaultMaterialBindGroup) {
    this.#renderer = renderer;
    this.#device = renderer.getDevice();
    this.#vb = this.#renderer.getVB();
    this.#pointDataBuffer = this.#renderer.getPointDataBuffer();
    this.#defaultMaterialBindGroup = defaultMaterialBindGroup;
  }

  /**
   * Returns the bind group layout for GPU skinning (bind group 3). Created lazily.
   * The four read-only storage buffers match the layout produced by the skinning
   * controller in the GltfMeshLoader extension and consumed by skinning.wgsl.
   * @returns {GPUBindGroupLayout}
   */
  getSkinningBindGroupLayout() {
    if (!this.#skinningBindGroupLayout) {
      this.#skinningBindGroupLayout = this.#device.createBindGroupLayout({
        label: 'skinning bind group layout',
        entries: [
        { binding: 0, visibility: GPUShaderStage.VERTEX, buffer: { type: 'read-only-storage' } }, // skinningOffsets
        { binding: 1, visibility: GPUShaderStage.VERTEX, buffer: { type: 'read-only-storage' } }, // skinningVertices
        { binding: 2, visibility: GPUShaderStage.VERTEX, buffer: { type: 'read-only-storage' } }, // skinningMatrices
        { binding: 3, visibility: GPUShaderStage.VERTEX, buffer: { type: 'read-only-storage' } } // fragmentOffsets
        ]
      });
    }
    return this.#skinningBindGroupLayout;
  }

  #createPipeline(key, geometry, material, materialTextureMask, hasInstanceIdMap, useWboit, idOnly = false, ghosted = false) {
    const attributes = geometry.attributes;
    const includeVC = !!(attributes.color && material.vertexColors);
    const isUVW = !!attributes.uvw;
    const hasUV = !!attributes.uv || isUVW;
    const hasTextures = hasUV && materialTextureMask !== 0;
    const hasWideLines = material.wideLines && isValidWideLineGeometry(geometry);
    // Skip normals for THREE.BasicMaterial which signals that rendering without lighting is desired (e.g. gizmos).
    const isUnorm16 = geometry.hasUnorm16Layout?.();
    const includeNormals = (!!attributes.normal || isUnorm16) && !(material instanceof THREE.MeshBasicMaterial);

    let pipeline;

    const depthCompare = idOnly || material.depthTest ? invertDepthCompare(material.depthFunc || 'less-equal') : 'always';
    const categoryHighlightingEnabled = this.#renderer.isCategoryHighlightingEnabled();
    const categoryHighlightingFallback = this.#renderer.isCategoryHighlightingFallback();
    if (geometry.isLines) {

      let shader = this.#device.createShaderModule({
        label: 'basic shader 3D lines',
        code: getBasicShader(includeVC, hasWideLines, hasInstanceIdMap, categoryHighlightingEnabled, categoryHighlightingFallback, false, false, isUnorm16)
      });

      const sampleCount = this.#renderer.getMSAASampleCount();
      pipeline = this.#device.createRenderPipeline({
        label: 'uber pipeline 3D lines',
        layout: this.#device.createPipelineLayout({
          bindGroupLayouts: this.#activeBindGroupLayout
        }),
        vertex: {
          module: shader,
          entryPoint: 'vsmain',
          buffers: getBufferLayout(geometry, false, false, includeVC, false, false)
        },
        fragment: {
          module: shader,
          entryPoint: 'psmain',
          targets: this.#activeTargetsList
        },
        primitive: {
          topology: 'line-list',
          cullMode: 'none'
        },
        depthStencil: {
          depthWriteEnabled: useWboit ? false : material.depthWrite,
          depthCompare,
          format: DepthFormat
        },
        multisample: {
          count: sampleCount
        }
      });
    } else if (geometry.isPoints) {
      // Determine point data layout from geometry attributes
      const colorAttr = attributes.color;
      const pointStride = geometry.vbstride;
      const colorOffset = colorAttr?.offset ?? 3;
      const isByteColor = colorAttr?.bytesPerItem === 1;
      // Points generate UV from quad corners — no geometry uv attr required.
      const pointsHasTextures = materialTextureMask !== 0;

      let shader = this.#device.createShaderModule({
        label: 'basic shader points',
        code: getBasicShader(includeVC, false, hasInstanceIdMap, categoryHighlightingEnabled, categoryHighlightingFallback, false, pointsHasTextures, false, /*useWboit=*/false, true, pointStride, colorOffset, isByteColor)
      });

      const pointsBindGroupLayouts = [...this.#activeBindGroupLayout, this.#pointDataBindGroupLayout];

      const sampleCount = this.#renderer.getMSAASampleCount();
      const pipelineConstants = { ghosted };

      pipeline = this.#device.createRenderPipeline({
        label: ghosted ? 'uber pipeline points (ghosted)' : 'uber pipeline points',
        layout: this.#device.createPipelineLayout({
          bindGroupLayouts: pointsBindGroupLayouts
        }),
        vertex: {
          module: shader,
          entryPoint: 'vsmain',
          buffers: getBufferLayout(geometry, false, false, includeVC, false, false, true),
          constants: pipelineConstants
        },
        fragment: {
          module: shader,
          entryPoint: 'psmain',
          targets: this.#activeTargetsList,
          constants: pipelineConstants
        },
        primitive: {
          topology: 'triangle-list',
          cullMode: 'none'
        },
        depthStencil: {
          // Ghosted points don't write depth, mirroring WebGL's fadeMaterial
          depthWriteEnabled: useWboit || ghosted ? false : material.depthWrite,
          depthCompare,
          format: DepthFormat
        },
        multisample: {
          count: sampleCount
        }
      });
    } else {

      // Query per-material custom sampled texture (e.g. VirtualTexture) for its WGSL chunk.
      // Only the diffuse slot is currently supported. The framework owns the applicability
      // gate (#usesCustomTexture); getShaderChunk() is only called when it applies and is
      // contracted to always return a non-empty chunk.
      const customTex = material.customSampledTextures?.diffuse;
      const hasCustomTexture = UberPipeline.#usesCustomTexture(geometry, material);
      const customTexShaderChunk = hasCustomTexture ? customTex.getShaderChunk({ isUVW, includeNormals }) : '';

      let shader;

      if (includeNormals) {

        const { isGamma, isRGBM } = this.#renderer.getEnvMapEncoding();

        // Activate gamma-decoding for input colors if tone-mapping is used
        // (e.g., see inputToLinear() in uber.wgsl)
        const gammaInput = this.#renderer.getTonemapMethod() != ToneMapMethod.None;
        const useIrradianceMap = this.#renderer.getIBL().hasIrradianceMap();

        shader = this.#device.createShaderModule({
          label: 'uber shader triangles (' + key + ')',
          code: getUberShader(material, hasUV, hasTextures, includeVC, isUVW, isGamma, isRGBM, gammaInput,
          hasInstanceIdMap, useIrradianceMap, useWboit, categoryHighlightingEnabled, categoryHighlightingFallback,
          idOnly, isUnorm16, customTexShaderChunk, !!material.hasSkinning)
        });
      } else {
        // Fallback for shapes without normals (e.g. gizmos with UnlitMaterial).
        shader = this.#device.createShaderModule({
          label: 'basic shader triangles (' + key + ')',
          code: getBasicShader(includeVC, hasWideLines, hasInstanceIdMap, categoryHighlightingEnabled, categoryHighlightingFallback, hasUV, hasTextures, isUnorm16, useWboit)
        });
      }

      // "depthBias, depthBiasSlopeScale, and depthBiasClamp have no effect on 'point-list', 'line-list', and
      // 'line-strip' primitives, and must be 0." ...from https://www.w3.org/TR/webgpu/#depth-stencil-state
      const { depthBias, depthBiasSlopeScale } = getDepthBiasValues(material);

      // Add custom-sampled-texture bind group layout when applicable. Slots 0-2 (frame,
      // render batch, material) always exist, so CustomTextureBindGroup (3) is the next
      // free slot.
      let bindGroupLayouts = this.#activeBindGroupLayout;
      if (hasCustomTexture) {
        bindGroupLayouts = [...this.#activeBindGroupLayout];
        bindGroupLayouts[CustomTextureBindGroup] = customTex.getBindGroupLayout();
      } else if (material.hasSkinning) {
        // GPU skinning occupies the same slot (3) as custom textures; the two are
        // mutually exclusive (skinned glTF meshes don't use custom sampled textures).
        bindGroupLayouts = [...this.#activeBindGroupLayout];
        bindGroupLayouts[SkinningBindGroup] = this.getSkinningBindGroupLayout();
      }

      const sampleCountTris = this.#renderer.getMSAASampleCount();
      const pipelineDesc = {
        label: 'uber pipeline triangles (' + key + ')',
        layout: this.#device.createPipelineLayout({
          bindGroupLayouts
        }),
        vertex: {
          module: shader,
          entryPoint: 'vsmain',
          buffers: getBufferLayout(geometry, includeNormals, hasUV, includeVC, isUVW, hasWideLines)
        },
        fragment: {
          module: shader,
          entryPoint: idOnly ? 'idOnlyPsMain' : 'psmain',
          targets: this.#activeTargetsList
        },
        primitive: {
          topology: 'triangle-list',
          cullMode: sideToCullMode(material.side)
        },

        depthStencil: {
          depthWriteEnabled: idOnly ? true : useWboit ? false : material.depthWrite,
          depthCompare,
          format: DepthFormat,
          depthBias,
          depthBiasSlopeScale
        },
        multisample: {
          count: sampleCountTris
        }
      };
      pipeline = this.#device.createRenderPipeline(pipelineDesc);
    }

    const pipelines = ghosted ? this.#ghostedPipelines : this.#pipelines;
    pipelines.set(key, pipeline);

    return pipeline;
  }

  resetPipelines() {
    this.#pipelines.clear();
    this.#ghostedPipelines.clear();
  }

  /**
   * @param {GPUBindGroupLayout[]} layouts
   * @param {GPUColorTargetState[]} targets
   */
  reset(layouts, targets, pointDataBindGroupLayout) {
    this.#activeBindGroupLayout = layouts;
    this.#activeTargetsList = targets;
    this.#pointDataBindGroupLayout = pointDataBindGroupLayout;
  }

  #activateMaterialBindings(passEncoder, material) {

    let materialUniformsMask = material.__gpuUniformsMask | 0;

    if (materialUniformsMask === MaterialUniformFlags.NO_UNIFORMS && !material.needsUpdate) {
      // Reset to default placeholder to avoid stale bindGroup from previous textured material
      passEncoder.setBindGroupIfNeeded(2, this.#defaultMaterialBindGroup);
      return 0;
    }

    if (materialUniformsMask === 0 || material.needsUpdate) {
      materialUniformsMask = initMaterialBindings(this.#device, material, this.#renderer.getPlaceholderTexture());
    } else if (material.__gpuMaterialUniforms) {
      // offset/repeat live on the texture but texMatrix is a material uniform — refresh each
      // draw so changes appear without material.needsUpdate (matches WebGL per-draw refresh).
      material.__gpuMaterialUniforms.updateUniformBuffer(material);
      // wrap/filter/anisotropy live on the GPUSampler — refresh bind group when they change.
      syncMaterialTextureBindings(material);
    }

    if (materialUniformsMask === MaterialUniformFlags.NO_UNIFORMS) {
      // Reset to default placeholder to avoid stale bindGroup from previous textured material
      passEncoder.setBindGroupIfNeeded(2, this.#defaultMaterialBindGroup);
      return 0;
    }

    passEncoder.setBindGroupIfNeeded(2, material.__gpuMaterialUniforms.getBindGroup());

    return materialUniformsMask;
  }

  /**
   * Unified check to decide whether a custom sampled texture (e.g. VirtualTexture) is
   * applied to a given (geometry, material). Custom textures need UVs to sample and are only
   * injected into the lit triangle uber shader, so they apply only to UV-bearing triangle
   * geometry rendered with normals. Used identically for the pipeline cache key, the pipeline
   * bind-group layout, and the draw-time bind-group, so the three can never diverge.
   *
   * TODO: Extend to the unlit basic shader (getBasicShader / basic.wgsl) so custom textures
   * also work on MeshBasicMaterial and normal-less geometry. Requires mirroring the uber.wgsl
   * injection points in basic.wgsl (which is shared with lines/points and has no uvw path).
   *
   * @param {BufferGeometry} geometry
   * @param {Material} material
   * @returns {boolean}
   */
  static #usesCustomTexture(geometry, material) {
    if (!material.customSampledTextures?.diffuse) {
      return false;
    }
    const attributes = geometry.attributes;
    const hasUV = !!attributes.uv || !!attributes.uvw;
    // Only the lit triangle path (getUberShader) injects the custom-texture chunk; the
    // unlit basic shader path does not (yet) — see TODO above.
    const includeNormals = (!!attributes.normal || geometry.hasUnorm16Layout?.()) && !(material instanceof THREE.MeshBasicMaterial);
    return hasUV && includeNormals && !geometry.isLines && !geometry.isPoints;
  }

  #preparePipeline(passEncoder, geometry, material, hasInstanceIdMap, useWboit, idOnly = false, ghosted = false) {
    const materialUniformsMask = this.#activateMaterialBindings(passEncoder, material);
    const materialTextureMask = materialUniformsMask & MaterialUniformFlags.TEXTURE_MASK;

    const attributes = geometry.attributes;
    const includeVC = attributes.color && material.vertexColors;
    const hasUV = attributes.uv || attributes.uvw;
    const hasTextures = (hasUV || geometry.isPoints) && materialTextureMask !== 0;
    const hasWideLines = Boolean(material.wideLines) && isValidWideLineGeometry(geometry);
    const hasCustomTexture = UberPipeline.#usesCustomTexture(geometry, material);
    const key = getPipelineHash(geometry, material, hasUV, includeVC, hasTextures, hasWideLines, hasInstanceIdMap, useWboit, idOnly, hasCustomTexture, !!material.hasSkinning);

    const pipelines = ghosted ? this.#ghostedPipelines : this.#pipelines;
    let pipeline = pipelines.get(key);
    if (!pipeline) {
      pipeline = this.#createPipeline(key, geometry, material, materialTextureMask, hasInstanceIdMap, useWboit, idOnly, ghosted);
    }

    passEncoder.setPipelineIfNeeded(pipeline);

    // Bind the per-material custom sampled texture (group 3) when applicable.
    if (hasCustomTexture) {
      passEncoder.setBindGroupIfNeeded(CustomTextureBindGroup, material.customSampledTextures.diffuse.getBindGroup());
    }

    return materialTextureMask;
  }

  drawOne(passEncoder, objectIndex, geometry, material, useWboit, idOnly = false) {
    // Don't draw geometry that is marked as wideLines but invalid to prevent it from drawing as normal triangles.
    if (geometry.isWideLines && !isValidWideLineGeometry(geometry)) {
      return 0;
    }

    const materialTextureMask = this.#preparePipeline(passEncoder, geometry, material, false, useWboit, idOnly);

    if (geometry.isPoints) {
      return this.#drawPoints(passEncoder, objectIndex, geometry, materialTextureMask);
    }
    this.#vb.draw(passEncoder, geometry, objectIndex);
    return materialTextureMask;
  }

  /**
   * Ghosted (dimmed) pass draw.
   * Currently only point geometry is handled here; other geometry is ghosted as edges by EdgePipeline.drawOneGhosted.
   *
   * @returns {number} materialTextureMask
   */
  drawOneGhosted(passEncoder, objectIndex, geometry, material) {
    if (!geometry.isPoints) {
      return 0;
    }
    const materialTextureMask = this.#preparePipeline(passEncoder, geometry, material, false, /*useWboit=*/false, false, /*ghosted=*/true);
    return this.#drawPoints(passEncoder, objectIndex, geometry, materialTextureMask);
  }

  /**
   * Shared point-draw sequence for the color and ghosted passes. Assumes the pipeline has already
   * been prepared (see #preparePipeline). Uploads/binds the point SSBO and issues the draw.
   * @returns {number} the passed-through materialTextureMask
   */
  #drawPoints(passEncoder, objectIndex, geometry, materialTextureMask) {
    // Ensure point SSBO is uploaded before creating the bind group
    this.#pointDataBuffer.ensurePointData(geometry);
    // Set the point data storage buffer bind group
    const bindGroup = this.#getOrCreatePointDataBindGroup(geometry);
    if (!bindGroup) {
      return materialTextureMask;
    }
    passEncoder.setBindGroupIfNeeded(3, bindGroup);
    this.#pointDataBuffer.drawPoints(passEncoder, geometry, objectIndex);
    return materialTextureMask;
  }

  /**
   * Get or create a bind group for the point data storage buffer.
   * Cached on the geometry object; invalidated when the SSBO is destroyed.
   * @param {BufferGeometry} geometry
   * @returns {GPUBindGroup|null}
   */
  #getOrCreatePointDataBindGroup(geometry) {
    if (geometry.__gpuPointDataBindGroup) {
      return geometry.__gpuPointDataBindGroup;
    }

    const buffer = geometry.__gpuPointSSBO;
    if (!buffer) {
      console.error('Point geometry missing storage buffer');
      return null;
    }

    geometry.__gpuPointDataBindGroup = this.#device.createBindGroup({
      label: 'point data bind group',
      layout: this.#pointDataBindGroupLayout,
      entries: [{
        binding: 0,
        resource: {
          buffer,
          offset: geometry.__gpuPointSSBOOffset,
          size: geometry.__gpuPointSSBOSize
        }
      }]
    });

    return geometry.__gpuPointDataBindGroup;
  }

  /**
   * @param {GPUCommandEncoder} passEncoder
   * @param {MultiDrawCall} multiDrawCall
   * @param {MultiDrawBatch} mdBatch
   * @param {number} multiDrawIndex
   * @param {boolean} useWboit
   * @param {boolean} idOnly - When true, render into the ID pass using idMaterial instead of the batch material.
   * @param {Material|null} idMaterial - Id-only material override, required when idOnly is set.
   */
  drawMulti(passEncoder, multiDrawCall, mdBatch, multiDrawIndex, useWboit = false, idOnly = false, idMaterial = null) {
    // Why is it okay to use just the first geometry/material as reference for the whole batch?
    // The code that creates the MultiDrawBatch (see MultiDrawRecorder.#needsNewMultiDraw) is
    // responsible for ensuring all members share the same pipeline-relevant state — pipeline
    // hash, vertex/index buffers, and custom sampled texture.
    const geometry = multiDrawCall.firstGeometry;

    // The ID pass only rasterizes selectable surfaces; skip line geometry (mirrors the
    // per-object id loop in MainPass which guards on !geom.isLines).
    if (idOnly && geometry.isLines) {
      return 0;
    }

    const material = idOnly ? idMaterial : multiDrawCall.firstMaterial;
    const hasInstanceIdMap = mdBatch.enableCulling;
    const materialTextureMask = this.#preparePipeline(passEncoder, geometry, material, hasInstanceIdMap, useWboit, idOnly);

    this.#vb.drawMulti(passEncoder, multiDrawCall, mdBatch, multiDrawIndex);

    return materialTextureMask;
  }
}